import {
  booleanAttribute,
  Component,
  computed,
  ElementRef,
  input,
  linkedSignal,
  numberAttribute,
  output,
  viewChildren,
} from '@angular/core';

import { MibIconComponent } from '../icons';
import { MibTooltipDirective, TooltipPosition } from '../tooltip';
import { SEGMENT_SIZE } from './button-toggles.config';
import { MibButtonTogglesSegment, MibButtonTogglesSize } from './button-toggles.models';

/**
 * Row of joined segments showing or hiding the zones of a page.
 *
 * By default one segment is selected at a time. Add {@link multiple} to let the segments toggle
 * independently, which is what the zone group of a page needs: several zones displayed at once, and
 * never fewer than {@link minActive}, so the page can never end up empty.
 *
 * Segments are `aria-pressed` toggle buttons inside a plain `role="group"` rather than radios, in
 * both modes: the choice is a display preference the user pushes on and off, not a value being
 * filled in, and the markup must not change meaning when `multiple` is flipped.
 *
 * The component is controlled: it never mutates {@link value}, it emits the value the caller should
 * apply. This keeps the group and the layout state it drives from drifting apart.
 */
@Component({
  selector: 'mib-button-toggles',
  templateUrl: './button-toggles.component.html',
  imports: [MibIconComponent, MibTooltipDirective],
  host: {
    role: 'group',
    class: 'box-border inline-flex items-stretch overflow-hidden rounded-mib border border-mib-hairline bg-white',
    '[class]': 'hostClasses()',
    '[attr.aria-label]': 'ariaLabel()',
    '(keydown)': 'onKeydown($event)',
  },
})
export class MibButtonTogglesComponent {
  /** Zones of the page, in the order they are rendered. */
  readonly segments = input.required<MibButtonTogglesSegment[]>();
  /** Ids of the zones currently displayed. */
  readonly value = input.required<readonly string[]>();
  /** Accessible name of the group, e.g. "Displayed zones". */
  readonly ariaLabel = input.required<string>({ alias: 'aria-label' });
  /** Lets several segments be active at once. Without it, selecting one deselects the others. */
  readonly multiple = input(false, { transform: booleanAttribute });
  /** Smallest number of zones that must stay displayed. Only applies when {@link multiple} is set. */
  readonly minActive = input(1);
  /** The step of the control scale every segment is drawn at. */
  readonly size = input<MibButtonTogglesSize>('md');
  /** Where the tooltip of a segment opens. The group usually sits in a bar, so below by default. */
  readonly tooltipPosition = input<TooltipPosition>('below');
  /** Delay before a segment tooltip opens, so sweeping across the group does not flash them all. */
  readonly tooltipShowDelay = input(500, { transform: numberAttribute });
  /** Disables the whole group, on top of the per-segment `disabled` flag. */
  readonly disabled = input(false, { transform: booleanAttribute });

  /** Emits the new list of displayed zone ids, in the declared segment order. */
  readonly valueChange = output<string[]>();

  private readonly segmentButtons = viewChildren<ElementRef<HTMLButtonElement>>('segmentButton');

  /**
   * Index of the only segment reachable with Tab (roving tabindex): the group is a single stop in the
   * page order, and the arrow keys move within it. It follows the segment list so that removing or
   * disabling the focused segment does not leave the group without a tab stop.
   */
  protected readonly focusedIndex = linkedSignal<MibButtonTogglesSegment[], number>({
    source: this.segments,
    computation: (segments, previous) => {
      const previousIndex = previous?.value ?? -1;
      if (previousIndex >= 0 && previousIndex < segments.length && !segments[previousIndex].disabled) {
        return previousIndex;
      }
      return Math.max(
        0,
        segments.findIndex((segment) => !segment.disabled),
      );
    },
  });

  /** The group's height carries its border, as a `[mibButton]` of the same size counts its own. */
  protected readonly hostClasses = computed(() => SEGMENT_SIZE[this.size()].height);

  protected readonly iconClass = computed(() => SEGMENT_SIZE[this.size()].icon);

  protected isActive(index: number): boolean {
    return this.value().includes(this.segments()[index].id);
  }

  protected isSegmentDisabled(index: number): boolean {
    return this.disabled() || !!this.segments()[index].disabled;
  }

  protected segmentClasses(index: number): string {
    const classes = [
      'flex',
      'items-center',
      'justify-center',
      'shrink-0',
      'transition-colors',
      // Segments stretch to the group's inner height; a label widens with its text on the button's gutter.
      ...(this.segments()[index].icon
        ? [SEGMENT_SIZE[this.size()].width]
        : [SEGMENT_SIZE[this.size()].text, 'px-3', 'font-medium', 'whitespace-nowrap']),
      // The ring is inset so it stays inside the clipped, rounded group box.
      'focus-visible:outline-2',
      'focus-visible:-outline-offset-2',
    ];

    /*
     Hairline between segments, drawn as a shadow so it never takes part in the layout, and in
     `mib-hairline` — the grey of the frame around the group. One line is one grey: `mib-divider`
     belongs to the rule of a surface, not to the inside of a control.

     Under the pointer the line takes the colour the segment is filling with, so it disappears into
     it and the segment reads as one block instead of a piece with a seam. It takes two rules and
     not one: the line lives on a segment's right edge, so the segment being pointed at owns the
     line on its right, and its left-hand neighbour owns the one on its left. Erasing only the first
     would leave it fenced on one side.
    */
    if (index < this.segments().length - 1) {
      classes.push('shadow-[inset_-1px_0_0_0_var(--color-mib-hairline)]');

      /* A segment that cannot be pressed never fills, so its line has nothing to disappear into. */
      if (!this.isSegmentDisabled(index)) {
        classes.push(
          this.isActive(index)
            ? 'hover:shadow-[inset_-1px_0_0_0_var(--color-mib-branding-700)]'
            : 'hover:shadow-[inset_-1px_0_0_0_var(--color-gray-100)]',
        );
      }

      /* The neighbour's line goes with whatever the segment on its right is filling with. */
      if (!this.isSegmentDisabled(index + 1)) {
        classes.push(
          this.isActive(index + 1)
            ? '[&:has(+*:hover)]:shadow-[inset_-1px_0_0_0_var(--color-mib-branding-700)]'
            : '[&:has(+*:hover)]:shadow-[inset_-1px_0_0_0_var(--color-gray-100)]',
        );
      }
    }

    if (this.isActive(index)) {
      classes.push('bg-mib-branding-900', 'text-white', 'focus-visible:outline-white');
    } else {
      classes.push('bg-white', 'text-gray-700', 'focus-visible:outline-mib-primary');
    }

    if (this.isSegmentDisabled(index)) {
      classes.push('cursor-default', 'opacity-40');
      return classes.join(' ');
    }

    /*
     Each state answers the pointer in its own colours, where both used to go to the same dark blue:
     a segment that is not selected would fill with it, which reads as being selected. Unselected is
     the filled `secondary` skin of the button — white, washed to gray-100, pressed at gray-200 —
     and selected lightens one step from its own ground. `duration-0` on the hover only: arrives at
     once, leaves faded, the pace the button skin sets.
    */
    classes.push('cursor-pointer', 'hover:duration-0');
    classes.push(
      ...(this.isActive(index)
        ? ['hover:bg-mib-branding-700', 'active:bg-mib-branding-900']
        : ['hover:bg-gray-100', 'active:bg-gray-200']),
    );

    return classes.join(' ');
  }

  protected toggle(index: number): void {
    const segment = this.segments()[index];
    if (this.isSegmentDisabled(index)) {
      return;
    }

    const displayed = this.value();

    if (!this.multiple()) {
      // Single selection: re-picking the active segment is a no-op, never a way to end up with none.
      if (displayed.length === 1 && displayed[0] === segment.id) {
        return;
      }
      this.valueChange.emit([segment.id]);
      return;
    }

    if (displayed.includes(segment.id)) {
      // Refuse to hide the last displayed zone: the segment keeps its state and nothing is emitted.
      if (displayed.length <= this.minActive()) {
        return;
      }
      this.valueChange.emit(displayed.filter((id) => id !== segment.id));
      return;
    }

    this.valueChange.emit(
      this.segments()
        .filter(({ id }) => id === segment.id || displayed.includes(id))
        .map(({ id }) => id),
    );
  }

  protected onKeydown(event: KeyboardEvent): void {
    const lastIndex = this.segments().length - 1;
    let target: number | null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        target = this.nextEnabledIndex(this.focusedIndex(), 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        target = this.nextEnabledIndex(this.focusedIndex(), -1);
        break;
      case 'Home':
        target = this.nextEnabledIndex(-1, 1);
        break;
      case 'End':
        target = this.nextEnabledIndex(lastIndex + 1, -1);
        break;
      default:
        return;
    }

    event.preventDefault();
    if (target !== null) {
      this.focusedIndex.set(target);
      this.segmentButtons()[target]?.nativeElement.focus();
    }
  }

  /** Walks the segments in `step` direction, wrapping around, and returns the first enabled one. */
  private nextEnabledIndex(from: number, step: number): number | null {
    const count = this.segments().length;
    for (let offset = 1; offset <= count; offset++) {
      const index = (((from + offset * step) % count) + count) % count;
      if (!this.isSegmentDisabled(index)) {
        return index;
      }
    }
    return null;
  }
}
